<template>
  <el-drawer
    custom-class="user-company-access-drawer"
    :visible.sync="visible"
    size="min(1120px, 96vw)"
    :wrapper-closable="false"
    :before-close="beforeClose"
    append-to-body
  >
    <template slot="title">
      <span class="access-title">公司范围配置 <span class="access-user"><strong class="access-user-name">{{ user.userName }}</strong> · {{ user.account }}</span></span>
    </template>
    <div class="company-access" v-loading="loading">
      <div class="access-content">
        <div class="scope-heading">
          <span>范围设置</span>
          <span class="access-note">订单按公司、品牌及省市区范围筛选，充电桩按公司范围筛选。</span>
        </div>
        <el-radio-group v-model="config.allCompanies" size="small" :disabled="loading || saving">
          <el-radio-button :label="false">自定义范围</el-radio-button>
          <el-radio-button :label="true">不限制范围</el-radio-button>
        </el-radio-group>
        <span class="range-note">{{ config.allCompanies ? "公司、品牌和省市区均不限制" : "按公司、品牌分别设置省市区" }}</span>
        <el-button class="quick-entry" size="small" icon="el-icon-setting" :disabled="loading || saving || !!loadError" @click="$refs.quickScope.open(config)">统一快捷配置</el-button>
        <QuickCompanyScope ref="quickScope" :companies="companies" @apply="applyQuickScope" />

        <div v-if="loadError" class="access-error">
          <span>{{ loadError }}</span>
          <el-button type="text" @click="load">重新加载</el-button>
        </div>
        <div v-else class="access-selector">
          <section class="company-panel">
            <div class="selector-heading"><strong>公司</strong></div>
            <div class="company-settings">
              <div class="selection-toggle">
                <el-checkbox :value="config.allCompanies || allCompanyResultsSelected" :indeterminate="!config.allCompanies && someCompanyResultsSelected && !allCompanyResultsSelected" :disabled="config.allCompanies || saving || !selectableCompanyResults.length" @change="toggleCompanyResults">全部公司</el-checkbox>
                <span class="access-note">{{ companies.length > 0 && companyCount === companies.length ? '已全选' : '共 ' + companies.length + ' 个，已勾选 ' + companyCount }}</span>
              </div>
            </div>
            <div class="selector-search"><el-input v-model="companySearch" size="small" placeholder="搜索公司名称或编号" prefix-icon="el-icon-search" clearable /></div>
            <div class="company-list">
              <transition-group name="scope-option" tag="div">
              <div v-for="company in filteredCompanies" :key="company.companyId" class="company-row" :class="{ active: activeCompanyId === company.companyId }" @click="activate(company.companyId)">
                <el-checkbox
                  :value="config.allCompanies || !!selection(company.companyId)"
                  :disabled="config.allCompanies || saving"
                  :aria-label="'选择' + company.companyName"
                  @click.native.stop
                  @change="toggleCompany(company, $event)"
                />
                <div class="company-row-info">
                  <button type="button" class="company-name scope-name-button" :aria-pressed="activeCompanyId === company.companyId" @click.stop="activate(company.companyId)">{{ company.companyName }}</button>
                  <span class="company-description">{{ companySummary(company) }}</span>
                </div>
                <i class="el-icon-arrow-right"></i>
              </div>
              </transition-group>
              <div v-if="!filteredCompanies.length" class="selector-empty">暂无匹配公司</div>
            </div>
          </section>
          <section class="brand-panel">
            <template v-if="activeCompany">
              <div class="selector-heading"><strong>{{ activeCompany.companyName }}</strong><span>品牌</span></div>
              <div v-loading="brandsRefreshing" class="brand-content">
              <template v-if="!activeCompany.unavailable">
                <div class="brand-settings">
                  <div class="selection-toggle">
                    <el-checkbox :value="allActiveBrands || allBrandResultsSelected" :indeterminate="!allActiveBrands && selectedBrandCount > 0 && !allBrandResultsSelected" :disabled="config.allCompanies || saving" @change="setAllBrands">全部品牌</el-checkbox>
                    <span class="access-note">{{ activeCompany.brands.length > 0 && selectedBrandCount === activeCompany.brands.length ? '已全选' : '共 ' + activeCompany.brands.length + ' 个，已勾选 ' + selectedBrandCount }}</span>
                  </div>
                  <p class="access-note">{{ config.allCompanies ? "不限制品牌及地区，包含后续新增项" : "全选仅选择当前品牌，点击品牌设置地区" }}</p>
                </div>
                <div class="selector-search"><el-input v-model="brandSearch" size="small" placeholder="搜索该公司品牌" prefix-icon="el-icon-search" clearable /></div>
                <div class="brand-list">
                  <transition-group name="scope-option" tag="div">
                  <div v-for="brand in filteredBrands" :key="brand.brandId" class="brand-row" :class="{ active: activeBrandId === brand.brandId }" @click="activateBrand(brand.brandId)">
                    <el-checkbox :value="brandSelected(brand.brandId)" :disabled="config.allCompanies || saving" :aria-label="'选择' + brand.brandName" @click.native.stop @change="toggleBrand(brand.brandId, $event)" />
                    <div class="brand-row-info"><button type="button" class="scope-name-button" :aria-pressed="activeBrandId === brand.brandId" @click.stop="activateBrand(brand.brandId)">{{ brand.brandName }}</button><span class="company-description">{{ brandRegionSummary(brand.brandId) }}</span></div>
                    <i class="el-icon-arrow-right"></i>
                  </div>
                  </transition-group>
                </div>
                <div v-if="!filteredBrands.length" class="selector-empty">{{ activeCompany.brands.length ? '暂无匹配品牌' : '暂无车辆品牌' }}</div>
                <div v-if="unavailableBrands.length" class="unavailable-brands">
                  <p class="access-note">以下已选品牌已失效，请移除或改选全部品牌：</p>
                  <el-tag v-for="brand in unavailableBrands" :key="brand" type="info" size="small" :closable="!saving" @close="removeBrand(brand)">品牌 ID：{{ brand }}</el-tag>
                </div>
              </template>
              <div v-else class="selector-empty unselected-company">
                <p>公司已失效，请取消选择</p>
              </div>
              </div>
            </template>
            <div v-else class="selector-empty">请选择左侧公司</div>
          </section>
          <section class="region-panel">
            <div class="selector-heading"><strong>{{ activeBrand ? activeBrand.brandName : '省市区' }}</strong><span>地区范围</span></div>
            <div v-loading="regionsRefreshing" class="region-content">
            <template v-if="activeBrand && activeCompany && !activeCompany.unavailable">
              <BrandRegionEditor :value="activeRegionScope" :disabled="saving || config.allCompanies" @input="setBrandRegion" />
              <p class="access-note region-default-note">未单独设置的品牌默认全国；新品牌默认全国。</p>
            </template>
            <div v-else class="selector-empty">请选择公司及品牌</div>
            </div>
          </section>
        </div>
        <p class="access-note selection-summary" aria-live="polite">{{ summary }}</p>
      </div>
      <div class="access-footer">
        <span class="access-note">{{ dirty ? '有未保存的修改' : '公司、品牌及省市区独立配置' }}</span>
        <div><el-button size="small" :disabled="saving" @click="beforeClose">取消</el-button><el-button type="primary" size="small" :loading="saving" :disabled="loading || !!loadError" @click="save">保存配置</el-button></div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import BrandRegionEditor from "./BrandRegionEditor.vue";
import QuickCompanyScope from "./QuickCompanyScope.vue";
const emptyConfig = () => ({ allCompanies: false, companies: [] });
const baseUrl = "/v1/web/user-company-access";

export default {
  name: "UserCompanyAccess",
  components: { BrandRegionEditor, QuickCompanyScope },
  data() {
    return {
      visible: false, loading: false, saving: false, loadError: "", brandsRefreshing: false, regionsRefreshing: false,
      user: {}, companies: [], config: emptyConfig(), original: "",
      activeCompanyId: "", companySearch: "", brandSearch: "", loadSequence: 0, preferredCompanyId: "", activeBrandId: "",
    };
  },
  computed: {
    dirty() { return !!this.original && JSON.stringify(this.config) !== this.original; },
    filteredCompanies() {
      const query = this.companySearch.trim().toLowerCase();
      return this.companies.filter(company => (company.companyName + company.companyId).toLowerCase().includes(query));
    },
    selectableCompanyResults() { return this.filteredCompanies.filter(company => !company.unavailable); },
    allCompanyResultsSelected() { return this.selectableCompanyResults.length > 0 && this.selectableCompanyResults.every(company => !!this.selection(company.companyId)); },
    someCompanyResultsSelected() { return this.selectableCompanyResults.some(company => !!this.selection(company.companyId)); },
    companyCount() { return this.config.allCompanies ? this.companies.length : this.config.companies.length; },
    activeCompany() { return this.companies.find(company => company.companyId === this.activeCompanyId); },
    activeSelection() { return this.selection(this.activeCompanyId); },
    activeBrand() { return this.activeCompany && this.activeCompany.brands.find(brand => brand.brandId === this.activeBrandId); },
    activeRegionScope() {
      if (!this.brandSelected(this.activeBrandId)) return { brandId: this.activeBrandId, allRegions: false, regions: [] };
      return this.regionScope(this.activeBrandId) || { brandId: this.activeBrandId, allRegions: true, regions: [] };
    },
    allActiveBrands() { return this.config.allCompanies || !!(this.activeSelection && this.activeSelection.allBrands); },
    allBrandResultsSelected() { return !!this.activeCompany && this.activeCompany.brands.length > 0 && this.activeCompany.brands.every(brand => this.brandSelected(brand.brandId)); },
    selectedBrandCount() { return this.activeCompany ? this.activeCompany.brands.filter(brand => this.brandSelected(brand.brandId)).length : 0; },
    filteredBrands() {
      return this.activeCompany ? this.activeCompany.brands.filter(brand => brand.brandName.toLowerCase().includes(this.brandSearch.trim().toLowerCase())) : [];
    },
    unavailableBrands() {
      return !this.allActiveBrands && this.activeSelection && this.activeCompany
        ? this.activeSelection.brands.filter(brand => !this.activeCompany.brands.some(option => option.brandId === brand)) : [];
    },
    summary() {
      if (this.config.allCompanies) return "公司、品牌和省市区均不限制，包含后续新增项。";
      if (!this.config.companies.length) return "尚未配置公司，保存后该人员的公司及品牌配置为空。";
      const allBrands = this.config.companies.filter(company => company.allBrands).length;
      const restricted = this.config.companies.reduce((count, company) => count + company.brandRegions.filter(scope => !scope.allRegions && (company.allBrands || company.brands.includes(scope.brandId))).length, 0);
      return `已选 ${this.config.companies.length} 家公司 · ${allBrands} 家全部品牌 · ${restricted} 项品牌指定地区`;
    },
  },
  beforeDestroy() {
    clearTimeout(this._brandsRefreshTimer);
    clearTimeout(this._regionsRefreshTimer);
  },
  methods: {
    refreshChildren(includeBrands = false) {
      // 重复点击重新计时，避免上一次切换的定时器提前结束当前加载反馈。
      if (includeBrands) {
        clearTimeout(this._brandsRefreshTimer);
        this.brandsRefreshing = true;
        this._brandsRefreshTimer = setTimeout(() => { this.brandsRefreshing = false; }, 180);
      }
      clearTimeout(this._regionsRefreshTimer);
      this.regionsRefreshing = true;
      this._regionsRefreshTimer = setTimeout(() => { this.regionsRefreshing = false; }, 180);
    },
    activateBrand(brandId) {
      this.activeBrandId = brandId;
      this.refreshChildren();
    },
    applyQuickScope(companies) {
      if (this.saving || this.loading || !companies.length) return;
      // 统一配置完整替换公司范围，未在本次选择的公司也一并取消。
      this.config = { allCompanies: false, companies };
      this.activate(companies[0].companyId);
      this.$message.success('已应用快捷配置，请保存配置');
    },
    open(user, companyId = "") {
      this.user = { id: user.id, userName: user.userName, account: user.account };
      this.preferredCompanyId = companyId;
      this.config = emptyConfig(); this.companies = []; this.original = "";
      this.companySearch = ""; this.brandSearch = ""; this.activeCompanyId = ""; this.activeBrandId = "";
      this.visible = true;
      this.load();
    },
    async load() {
      const requestId = ++this.loadSequence;
      this.loading = true; this.loadError = "";
      try {
        const responses = await Promise.all([this.axios.get(baseUrl + "/options"), this.axios.get(baseUrl + "/" + this.user.id)]);
        if (requestId !== this.loadSequence) return;
        for (const response of responses) {
          if (response.data.code !== 200) throw new Error(response.data.message || "加载失败");
        }
        this.companies = responses[0].data.data;
        this.config = responses[1].data.data;
        this.config.companies.forEach(company => {
          if (!company.brandRegions) this.$set(company, "brandRegions", []);
          if (!this.companies.some(option => option.companyId === company.companyId)) {
            this.companies.push({ companyId: company.companyId, companyName: "已失效公司 · " + company.companyId, brands: [], unavailable: true });
          }
        });
        // 自定义范围将历史“全部品牌”展开为当前品牌快照，保存后不隐式授权新增品牌。
        if (!this.config.allCompanies) this.config.companies.forEach(selected => {
          if (selected.allBrands) {
            const company = this.companies.find(item => item.companyId === selected.companyId);
            selected.brands = company ? company.brands.map(brand => brand.brandId) : [];
            selected.allBrands = false;
          }
        });
        // 只在打开并加载配置后按初始勾选状态排序，后续操作和切换父级均保留此顺序。
        this.companies.sort((a, b) => Number(this.config.allCompanies || !!this.selection(b.companyId)) - Number(this.config.allCompanies || !!this.selection(a.companyId)));
        this.companies.forEach(company => {
          const selected = this.selection(company.companyId);
          const isSelected = brand => this.config.allCompanies || !!(selected && (selected.allBrands || selected.brands.includes(brand.brandId)));
          company.brands.sort((a, b) => Number(isSelected(b)) - Number(isSelected(a)));
        });
        const firstSelected = this.companies.find(company => company.companyId === this.preferredCompanyId)
          || this.companies.find(company => this.selection(company.companyId));
        this.activate(firstSelected ? firstSelected.companyId : (this.companies[0] || {}).companyId);
        this.original = JSON.stringify(this.config);
      } catch (error) {
        if (requestId === this.loadSequence) this.loadError = error.message || "加载失败，请重试";
      } finally { if (requestId === this.loadSequence) this.loading = false; }
    },
    selection(companyId) { return this.config.companies.find(company => company.companyId === companyId); },
    activate(companyId) {
      this.refreshChildren(true);
      this.activeCompanyId = companyId; this.brandSearch = "";
      const company = this.companies.find(item => item.companyId === companyId);
      const selected = this.selection(companyId);
      const brand = company && (company.brands.find(item => selected && selected.brands.includes(item.brandId)) || company.brands[0]);
      this.activeBrandId = brand ? brand.brandId : "";
    },
    brandSelected(brandId) { return this.allActiveBrands || !!(this.activeSelection && this.activeSelection.brands.includes(brandId)); },
    regionScope(brandId) { return this.activeSelection && this.activeSelection.brandRegions.find(scope => scope.brandId === brandId); },
    brandRegionSummary(brandId) {
      if (!this.brandSelected(brandId)) return "未选择";
      const scope = this.regionScope(brandId);
      return !scope || scope.allRegions ? "全国" : scope.regions.length ? `已选 ${scope.regions.length} 个地区` : "待选择地区";
    },
    toggleBrand(brandId, checked) {
      if (this.saving || this.config.allCompanies || !this.activeCompany || this.activeCompany.unavailable) return;
      // 全部品牌模式下允许取消单项，先展开当前品牌再切换为自定义选择。
      const brands = new Set(this.allActiveBrands ? this.activeCompany.brands.map(brand => brand.brandId) : (this.activeSelection ? this.activeSelection.brands : []));
      if (this.activeSelection) this.activeSelection.allBrands = false;
      checked ? brands.add(brandId) : brands.delete(brandId);
      this.setBrands([...brands]);
      this.activeBrandId = brandId;
      this.refreshChildren();
    },
    setBrandRegion(scope) {
      if (this.saving || this.config.allCompanies || !this.activeBrand || scope.brandId !== this.activeBrandId) return;
      const selected = this.ensureActiveSelection();
      if (!selected) return;
      // 地区操作自动关联父级；仅浏览公司或品牌时不勾选，也不修改原范围。
      if (!selected.allBrands && !selected.brands.includes(scope.brandId)) selected.brands.push(scope.brandId);
      const other = selected.brandRegions.filter(item => item.brandId !== scope.brandId);
      selected.brandRegions = scope.allRegions ? other : [...other, scope];
    },
    companySummary(company) {
      if (company.unavailable) return "公司已失效，请取消选择";
      const selected = this.selection(company.companyId);
      if (this.config.allCompanies || (selected && selected.allBrands)) return "全部品牌";
      return selected ? `已选 ${selected.brands.length} 个品牌` : `${company.brands.length} 个品牌 · 未选择`;
    },
    toggleCompany(company, checked) {
      if (this.saving || this.config.allCompanies) return;
      if (checked && !this.selection(company.companyId)) {
        if (company.unavailable) return;
        this.config.companies.push({ companyId: company.companyId, allBrands: false, brands: company.brands.map(brand => brand.brandId), brandRegions: [] });
      } else if (!checked) {
        this.config.companies = this.config.companies.filter(item => item.companyId !== company.companyId);
      }
      this.activate(company.companyId);
    },
    toggleCompanyResults(checked) {
      if (this.saving || this.config.allCompanies) return;
      // 只切换当前搜索结果，保留其他公司及其已有品牌、地区配置。
      if (checked) {
        this.selectableCompanyResults.forEach(company => {
          if (!this.selection(company.companyId)) this.config.companies.push({ companyId: company.companyId, allBrands: false, brands: company.brands.map(brand => brand.brandId), brandRegions: [] });
        });
      } else {
        const ids = new Set(this.selectableCompanyResults.map(company => company.companyId));
        this.config.companies = this.config.companies.filter(company => !ids.has(company.companyId));
      }
    },
    ensureActiveSelection() {
      if (this.saving || this.config.allCompanies || !this.activeCompany || this.activeCompany.unavailable) return null;
      // 浏览公司不改变配置；首次选择品牌时才建立该公司的范围。
      if (!this.activeSelection) this.config.companies.push({ companyId: this.activeCompanyId, allBrands: false, brands: [], brandRegions: [] });
      return this.activeSelection;
    },
    setAllBrands(value) {
      const selected = this.ensureActiveSelection();
      if (selected) {
        selected.allBrands = false;
        selected.brands = value ? this.activeCompany.brands.map(brand => brand.brandId) : [];
      }
    },
    setBrands(brands) {
      if (!brands.length && !this.activeSelection) return;
      const selected = this.ensureActiveSelection();
      if (selected) selected.brands = brands;
    },
    removeBrand(brand) { this.setBrands(this.activeSelection.brands.filter(item => item !== brand)); },
    async beforeClose(done) {
      if (this.saving) return;
      if (this.dirty) {
        try { await this.$confirm("修改尚未保存，确定放弃修改？", "提示", { confirmButtonText: "放弃修改", cancelButtonText: "继续编辑", type: "warning" }); }
        catch (error) { return; }
      }
      if (typeof done === "function") done();
      else this.visible = false;
    },
    async save() {
      if (this.saving || this.loading || this.loadError) return;
      const invalid = !this.config.allCompanies && this.config.companies.find(company =>
        !company.allBrands && !company.brands.length);
      if (invalid) {
        this.activate(invalid.companyId);
        this.$message.warning("请为公司选择至少一个品牌，或选择全部品牌");
        return;
      }
      if (!this.config.allCompanies) {
        for (const company of this.config.companies) {
          const invalidRegion = company.brandRegions.find(scope => !scope.allRegions && !scope.regions.length && (company.allBrands || company.brands.includes(scope.brandId)));
          if (invalidRegion) {
            this.activate(company.companyId); this.activeBrandId = invalidRegion.brandId;
            this.$message.warning("请为品牌选择省市区，或选择全国");
            return;
          }
        }
      }
      const payload = {
        allCompanies: this.config.allCompanies,
        companies: this.config.allCompanies ? [] : this.config.companies.map(company => ({
          companyId: company.companyId, allBrands: company.allBrands,
          brands: company.allBrands ? [] : company.brands,
          brandRegions: company.brandRegions.filter(scope => !scope.allRegions && (company.allBrands || company.brands.includes(scope.brandId))),
        })),
      };
      this.saving = true;
      try {
        const response = await this.axios.put(baseUrl + "/" + this.user.id, payload);
        if (response.data.code !== 200) throw new Error(response.data.message || "保存失败");
        this.original = JSON.stringify(this.config);
        if (String(this.user.id) === sessionStorage.getItem("userId")) {
          this.$store.dispatch("setCompany");
          this.$store.dispatch("setBrand");
        }
        this.$message.success("公司、品牌及省市区配置已保存");
        this.$emit("saved");
        this.visible = false;
      } catch (error) { this.$message.error(error.message || "保存失败，请重试"); }
      finally { this.saving = false; }
    },
  },
};
</script>

<style scoped>
.access-title { font-size: 16px; color: #303133; }
.access-user { margin-left: 12px; color: #73767a; font-size: 13px; }
.access-user-name { font-weight: 600; color: #303133; }
.company-access { height: 100%; display: flex; flex-direction: column; color: #303133; }
.access-content { flex: 1; min-height: 0; overflow-y: auto; padding: 0 20px 12px; }
.access-note { color: #73767a; font-size: 12px; font-weight: 400; line-height: 20px; margin: 0; }
.scope-heading { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 8px; font-size: 14px; font-weight: 600; margin: 0 0 8px; }
.range-note { display: inline-block; color: #73767a; font-size: 12px; margin: 6px 0 6px 10px; }
.quick-entry { margin-left: 12px; }
.access-selector { display: grid; grid-template-columns: minmax(210px, 1fr) minmax(210px, 1fr) minmax(300px, 1.35fr); height: calc(100vh - 220px); min-height: 360px; border: 1px solid #ebeef5; border-radius: 6px; margin-top: 12px; overflow: hidden; }
.company-panel { display: flex; flex-direction: column; min-height: 0; border-right: 1px solid #ebeef5; }
.company-panel > .selector-heading, .company-panel > .selector-search, .company-panel > .company-settings { flex-shrink: 0; }
.selector-heading { box-sizing: border-box; min-height: 36px; padding: 6px 12px; line-height: 22px; display: flex; justify-content: space-between; gap: 12px; align-items: center; background: #fafafa; font-size: 14px; }
.selector-heading span { color: #73767a; font-size: 12px; flex-shrink: 0; }
.selector-search { padding: 8px 12px 4px; }
.company-list { flex: 1; height: 0; min-height: 0; overflow-y: auto; margin-bottom: 8px; }
.company-row { display: flex; align-items: center; gap: 10px; padding: 0 12px 0 16px; border-top: 1px solid #f0f2f5; }
.company-row:hover { background: #fafafa; }
.company-row.active { background: #ecf5ff; }
.company-row > i { color: #909399; font-size: 12px; }
.company-row-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; background: transparent; border: 0; text-align: left; font: inherit; padding: 12px 0; }
.scope-name-button { align-self: flex-start; max-width: 100%; border: 0; padding: 0; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; overflow-wrap: anywhere; }
.scope-name-button:focus-visible { outline: 2px solid #409eff; outline-offset: 3px; }
.company-name { color: #303133; font-size: 13px; overflow-wrap: anywhere; }
.company-description { font-size: 12px; color: #73767a; }
.brand-panel { min-width: 0; min-height: 0; display: flex; flex-direction: column; border-right: 1px solid #ebeef5; }
.brand-panel > .selector-heading { flex-shrink: 0; }
.brand-content { flex: 1; min-height: 0; display: flex; flex-direction: column; }
.brand-content > :not(.brand-list) { flex-shrink: 0; }
.region-panel { min-width: 0; min-height: 0; display: flex; flex-direction: column; }
.region-panel > .selector-heading { flex-shrink: 0; }
.region-content { flex: 1; min-height: 0; overflow-y: auto; }
.region-default-note { padding: 0 12px 10px; }
.company-settings, .brand-settings { padding: 10px 12px 4px; }
.selection-toggle { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 10px; }
.selection-toggle .el-checkbox { margin: 0; }
.brand-settings p { margin-top: 4px; }
.brand-list { flex: 1; min-height: 0; overflow-y: auto; margin-bottom: 8px; }
.brand-row { display: flex; align-items: center; gap: 10px; padding: 0 12px 0 16px; border-top: 1px solid #f0f2f5; }
.brand-row:hover { background: #fafafa; }
.brand-row.active { background: #ecf5ff; }
.brand-row .el-checkbox { margin: 0; }
.brand-row > i { color: #909399; font-size: 12px; }
.brand-row-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; background: transparent; border: 0; text-align: left; font: inherit; font-size: 13px; padding: 12px 0; }
.selector-empty { text-align: center; color: #73767a; font-size: 13px; padding: 20px 12px; }
.unselected-company { padding-top: 40px; }
.unavailable-brands { padding: 0 12px 10px; }
.unavailable-brands .el-tag { margin: 8px 8px 0 0; }
.selection-summary { margin-top: 8px; }
.access-footer { flex-shrink: 0; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 20px; border-top: 1px solid #ebeef5; background: #fff; }
.access-error { padding: 20px 0; color: #f56c6c; }
@media (max-width: 800px) {
  .access-selector { grid-template-columns: repeat(2, minmax(0, 1fr)); height: auto; }
  .company-panel, .brand-panel { height: 360px; }
  .brand-panel { border-right: 0; }
  .region-panel { grid-column: 1 / -1; border-top: 1px solid #ebeef5; min-height: 200px; }
}
@media (max-width: 620px) {
  .access-selector { grid-template-columns: 1fr; }
  .company-panel { border-right: 0; border-bottom: 1px solid #ebeef5; }
  .company-list { flex: none; height: auto; max-height: 220px; }
  .access-content { padding: 0 12px 10px; }
  .access-footer { flex-wrap: wrap; padding: 12px 16px; }
  .range-note { margin-left: 0; display: block; }
}
.scope-option-enter-active, .scope-option-leave-active { transition: opacity .24s ease; }
.scope-option-enter, .scope-option-leave-to { opacity: 0; }
.scope-option-move { transition: transform .28s ease; animation: scope-option-fade .28s ease; }
@keyframes scope-option-fade { 0%, 100% { opacity: 1; } 45% { opacity: .3; } }
@media (prefers-reduced-motion: reduce) {
  .scope-option-enter-active, .scope-option-leave-active, .scope-option-move { transition: none; animation: none; }
}
</style>

<style>
.user-company-access-drawer .el-drawer__header {
  margin-bottom: 12px;
  padding: 14px 20px 0;
}
</style>
